import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import { setSessionStorage } from '@tcp/core/src/utils/utils.web';
import { getIconPath } from '@tcp/core/src/utils';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Button from '@tcp/core/src/components/common/atoms/Button';
import ClickTracker from '@tcp/web/src/components/common/atoms/ClickTracker';
import { convertHtml } from '@tcp/core/src/components/features/CnC/LoyaltyBanner/util/utility';
import { getPLCCNoThanksPayload, getApplyOrAcceptPayload } from '@tcp/core/src/constants/analytics';
import {
  StyledModal,
  StyledImage,
  StyledImageWrapper,
  SaveTodayWrapper,
} from '../styles/GlobalRtpsModal.style';
import BodyCopy from '../../../atoms/BodyCopy';
import CheckoutDynamicStore from '../../../../features/CnC/Checkout/container/CheckoutDynamicStore';
import ApplyCardDynamicStore from '../../../../features/browse/ApplyCardPage/container/ApplyCardDynamicStore';
import GlobalRtpsModalLabels, { GLOBAL_LABELS } from '../labels/GlobalRtpsModal.labels';
import {
  getDynamicLabelValues,
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../utils/labels/labelUtilty';

const noop = () => {
  // Empty function
};

const GlobalRtpsModal = ({
  isRtpsModalOpen,
  firstName,
  toggleRtpsModal,
  isRtpsFlow,
  setIsRTPSFlowActn,
  submitAcceptOrDeclinePlcc,
  resetPLCCApplicationStatus,
}) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, GlobalRtpsModalLabels);
  const { emailSignup, minibag, plccForm, accountDrawer } = globalLabels;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isOpen = isRtpsModalOpen && isModalOpen;
  const handleRtpsClose = () => {
    setIsModalOpen(false);
    setSessionStorage({ key: 'rtpsModalTimeStamp', value: Date.now() });
  };

  const openPLCCModal = async (e) => {
    if (isRtpsFlow) await CheckoutDynamicStore();
    e.preventDefault();
    handleRtpsClose();
    await ApplyCardDynamicStore();
    toggleRtpsModal({ isPLCCModalOpen: true });
    resetPLCCApplicationStatus({ status: null });
    if (isRtpsFlow) {
      submitAcceptOrDeclinePlcc({ accepted: true, presentationInd: '1' });
    }
  };

  const setRTPSFlow = async (e) => {
    if (isRtpsFlow) await CheckoutDynamicStore();
    e.preventDefault();
    handleRtpsClose();
    if (isRtpsFlow && setIsRTPSFlowActn) {
      submitAcceptOrDeclinePlcc({
        accepted: false,
        presentationInd: '2',
      });
      setIsRTPSFlowActn(false);
    }
  };

  const setModalOpen = () => {
    if (window.pageYOffset > 1000) {
      setIsModalOpen(true);
    }
  };

  const throttled = useCallback(throttle(setModalOpen, 500), []);

  useEffect(() => {
    window.addEventListener('scroll', throttled);

    return () => window.removeEventListener('scroll', throttled);
  }, []);

  return isOpen ? (
    <StyledModal>
      <BodyCopy
        fontFamily="secondary"
        fontWeight="black"
        fontSize={['fs12', 'fs20', 'fs20']}
        color="white"
        className="rtps-modal-text rtps-first-name-text"
      >
        {`${renderLabel(minibag?.lbl_miniBag_hi, 'lbl_miniBag_hi')} ${firstName},`}
      </BodyCopy>
      <BodyCopy>
        <BodyCopy
          fontFamily="secondary"
          fontWeight="black"
          fontSize={['fs12', 'fs15', 'fs16']}
          color="white"
          className="rtps-modal-text"
        >
          {getDynamicLabelValues('dynamic.global_d.lbl_rtps_you_are')}

          {getDynamicLabelValues('dynamic.global_d.lbl_PLCC_approved_text')}
        </BodyCopy>
      </BodyCopy>
      <StyledImageWrapper>
        <StyledImage
          src={getIconPath('place-card-small')}
          alt="plcc-card"
          className="header-icon"
        />
      </StyledImageWrapper>
      <SaveTodayWrapper>
        <BodyCopy
          fontFamily="primary"
          fontWeight="black"
          fontSize={['fs22', 'fs26', 'fs26']}
          color="white"
          className="rtps-modal-text rtps-save-today"
        >
          {getDynamicLabelValues('dynamic.global_d.lbl_PLCCOffer_modal_sub_header_1')}
        </BodyCopy>
        <BodyCopy
          fontFamily="primary"
          fontWeight="black"
          fontSize={['fs22', 'fs26', 'fs26']}
          color="white"
          className="rtps-modal-text rtps-save-today"
        >
          {getDynamicLabelValues('dynamic.global_d.lbl_PLCCOffer_modal_sub_header_2')}

          <BodyCopy fontSize={['fs6', 'fs8', 'fs8']} color="white" component="sup">
            {getDynamicLabelValues('dynamic.global_d.lbl_PLCCModal_applyNowHeaderTextSuffix')}
          </BodyCopy>
        </BodyCopy>
      </SaveTodayWrapper>
      <BodyCopy
        fontFamily="primary"
        fontWeight="normal"
        fontSize={['fs8', 'fs10', 'fs10']}
        color="white"
        className="rtps-modal-text"
      >
        {getDynamicLabelValues('dynamic.global_d.lbl_rtps_modal_plcc_text_1')}
      </BodyCopy>
      <BodyCopy
        fontFamily="secondary"
        fontWeight="black"
        fontSize={['fs8', 'fs10', 'fs10']}
        color="white"
        className="rtps-modal-text"
      >
        {renderLabel(
          accountDrawer?.lbl_accountDrawer_myPlaceRewardsCC,
          'lbl_accountDrawer_myPlaceRewardsCC'
        )}
      </BodyCopy>
      <ClickTracker isLoyaltyClick clickData={getApplyOrAcceptPayload('rtps-browse')}>
        <Button
          buttonVariation="fixed-width"
          fill="BLUE"
          type="submit"
          className="rtps-apply-now-button"
          onClick={openPLCCModal}
        >
          {renderLabel(plccForm?.lbl_PLCC_interested, 'lbl_PLCC_interested')}
        </Button>
      </ClickTracker>

      <BodyCopy className="rtps-no-thanks-link">
        <Anchor
          fontSizeVariation="large"
          anchorVariation="secondary"
          underline
          noLink
          target="_blank"
          handleLinkClick={setRTPSFlow}
        >
          <ClickTracker
            className="rtps-no-thanks"
            isLoyaltyClick
            clickData={getPLCCNoThanksPayload('rtps-browse')}
          >
            {renderLabel(emailSignup?.lbl_no_thanks, 'lbl_no_thanks')}
          </ClickTracker>
        </Anchor>
      </BodyCopy>
      <BodyCopy
        fontFamily="secondary"
        fontWeight="bold"
        fontSize={['fs8', 'fs10', 'fs10']}
        color="black"
        className="rtps-modal-terms"
      >
        {getDynamicLabelValues('dynamic.global_d.lbl_PLCC_terms_1')}
      </BodyCopy>
      <BodyCopy
        fontFamily="secondary"
        fontWeight="bold"
        fontSize={['fs8', 'fs10', 'fs10']}
        color="black"
        className="rtps-modal-terms"
      >
        {convertHtml(getDynamicLabelValues('dynamic.global_d.lbl_PLCC_terms_2'))}
      </BodyCopy>
    </StyledModal>
  ) : null;
};

GlobalRtpsModal.propTypes = {
  isRtpsModalOpen: PropTypes.bool,
  firstName: PropTypes.string,
  toggleRtpsModal: PropTypes.func,
  isRtpsFlow: PropTypes.bool,
  setIsRTPSFlowActn: PropTypes.func,
  submitAcceptOrDeclinePlcc: PropTypes.func,
  resetPLCCApplicationStatus: PropTypes.func,
};

GlobalRtpsModal.defaultProps = {
  isRtpsModalOpen: false,
  firstName: '',
  toggleRtpsModal: noop,
  isRtpsFlow: false,
  setIsRTPSFlowActn: noop,
  submitAcceptOrDeclinePlcc: noop,
  resetPLCCApplicationStatus: noop,
};

export default GlobalRtpsModal;
